import { useState, useEffect } from 'react'
import { X, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { generateApi } from '@/services/api'

interface GenerateHistoryModalProps {
  onClose: () => void
}

interface Generation {
  id: string
  type: string
  prompt: string
  negative_prompt?: string
  model: string
  points_used: number
  status: string
  error_message?: string
  created_at: string
}

export function GenerateHistoryModal({ onClose }: GenerateHistoryModalProps) {
  const [generations, setGenerations] = useState<Generation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedGen, setSelectedGen] = useState<Generation | null>(null)

  useEffect(() => {
    loadHistory()
  }, [])

  const loadHistory = async () => {
    setIsLoading(true)
    try {
      const result = await generateApi.getHistory()
      if (result.success && result.data) {
        setGenerations(result.data as unknown as Generation[])
      }
    } catch (error) {
      console.error('Failed to load history:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />
      case 'processing':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
      default:
        return <Clock className="w-4 h-4 text-gray-500" />
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'text2img':
        return 'Text-to-Image'
      case 'img2img':
        return 'Image-to-Image'
      default:
        return type
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-bg-primary rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-bg-primary border-b border-border-subtle p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-accent-purple" />
            <h2 className="text-xl font-bold">AI 생성 이력</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-bg-elevated rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-accent-gold" />
            </div>
          ) : generations.length === 0 ? (
            <div className="text-center py-12 text-text-tertiary">
              <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>아직 생성 이력이 없습니다</p>
            </div>
          ) : (
            <div className="space-y-3">
              {generations.map((gen) => (
                <div
                  key={gen.id}
                  className="border border-border-default rounded-lg p-4 hover:bg-bg-elevated transition-colors cursor-pointer"
                  onClick={() => setSelectedGen(gen)}
                >
                  <div className="flex items-start gap-3">
                    {/* Status Icon */}
                    <div className="mt-1">
                      {getStatusIcon(gen.status)}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-accent-gold">
                          {getTypeLabel(gen.type)}
                        </span>
                        <span className="text-xs text-text-tertiary">
                          {gen.model}
                        </span>
                        <span className="text-xs text-text-tertiary">
                          {gen.points_used} 포인트
                        </span>
                      </div>

                      <p className="text-sm text-text-secondary line-clamp-2 mb-2">
                        {gen.prompt}
                      </p>

                      <div className="flex items-center gap-4 text-xs text-text-tertiary">
                        <span>{formatDate(gen.created_at)}</span>
                        <span className={`px-2 py-0.5 rounded ${
                          gen.status === 'completed' ? 'bg-green-500/10 text-green-500' :
                          gen.status === 'failed' ? 'bg-red-500/10 text-red-500' :
                          'bg-blue-500/10 text-blue-500'
                        }`}>
                          {gen.status}
                        </span>
                      </div>

                      {gen.error_message && (
                        <p className="text-xs text-red-500 mt-2">
                          오류: {gen.error_message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {selectedGen && (
          <div
            className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
            onClick={() => setSelectedGen(null)}
          >
            <div
              className="bg-bg-primary rounded-lg shadow-xl max-w-2xl w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">생성 상세 정보</h3>
                <button
                  onClick={() => setSelectedGen(null)}
                  className="p-2 hover:bg-bg-elevated rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-text-tertiary">타입</label>
                  <p className="text-text-primary">{getTypeLabel(selectedGen.type)}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-text-tertiary">모델</label>
                  <p className="text-text-primary">{selectedGen.model}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-text-tertiary">프롬프트</label>
                  <p className="text-text-primary whitespace-pre-wrap">{selectedGen.prompt}</p>
                </div>

                {selectedGen.negative_prompt && (
                  <div>
                    <label className="text-sm font-medium text-text-tertiary">Negative 프롬프트</label>
                    <p className="text-text-primary whitespace-pre-wrap">{selectedGen.negative_prompt}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-text-tertiary">상태</label>
                  <p className="text-text-primary">{selectedGen.status}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-text-tertiary">사용 포인트</label>
                  <p className="text-text-primary">{selectedGen.points_used} 포인트</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-text-tertiary">생성 시간</label>
                  <p className="text-text-primary">{formatDate(selectedGen.created_at)}</p>
                </div>

                {selectedGen.error_message && (
                  <div>
                    <label className="text-sm font-medium text-text-tertiary">오류 메시지</label>
                    <p className="text-red-500">{selectedGen.error_message}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
